import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import useThemeColors from "../../hooks/useThemeColors";

const Terms: React.FC = () => {
  const colors = useThemeColors();

  return (
    <Container maxW="4xl" py={8}>
      <VStack gap={8} align="stretch">
        <Box textAlign="center">
          <Heading as="h1" size="xl" color={colors.text.primary} mb={4}>
            Terms of Service
          </Heading>
          <Text fontSize="lg" color={colors.text.secondary}>
            Last updated: 13/08/2025
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            1. Acceptance of Terms
          </Heading>
          <Text mb={4}>
            By accessing and using Result.ng ("the Service"), you accept and
            agree to be bound by the terms and provision of this agreement. If
            you do not agree to abide by the above, please do not use this
            service.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            2. Description of Service
          </Heading>
          <Text mb={4}>
            Result.ng is a comprehensive school management system designed to
            streamline administrative tasks, student management, and academic
            operations for educational institutions. The Service includes but is
            not limited to:
          </Text>
          <VStack gap={3} mb={4} align="start">
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Student Management:</strong> Complete student
                information management including personal details, academic
                records, and status tracking
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Guardian Management:</strong> Parent/guardian
                information management with multiple guardian support
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Academic Management:</strong> Subject management,
                assessment types, grade criteria, and behavioral tracking
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Results & Reporting:</strong> Comprehensive result
                management, bulk processing, and customizable report generation
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Dashboard & Analytics:</strong> Real-time performance
                metrics, visual analytics, and school statistics
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Notification System:</strong> Real-time notifications,
                announcements, and email integration
              </Text>
            </HStack>
            <HStack gap={2}>
              <CheckCircleIcon color="green.500" />
              <Text>
                <strong>Settings & Configuration:</strong> User settings, school
                branding, data management, and audit trails
              </Text>
            </HStack>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            3. User Accounts and Registration
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              To access the Service, you must register for an account. You agree
              to provide accurate, current, and complete information during
              registration and to update such information to keep it accurate,
              current, and complete.
            </Text>
            <Text>
              You are responsible for safeguarding the password and for all
              activities that occur under your account. You agree to notify us
              immediately of any unauthorized use of your account.
            </Text>
            <Text>
              You must be at least 18 years old or have parental consent to use
              the Service. Educational institutions must have proper
              authorization to use the Service on behalf of their organization.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            4. Acceptable Use Policy
          </Heading>
          <Text mb={4}>You agree not to use the Service to:</Text>
          <VStack gap={2} mb={4} align="start">
            <Text>
              • Upload, post, or transmit any content that is unlawful, harmful,
              threatening, abusive, or otherwise objectionable
            </Text>
            <Text>
              • Impersonate any person or entity or misrepresent your
              affiliation with any person or entity
            </Text>
            <Text>
              • Interfere with or disrupt the Service or servers connected to
              the Service
            </Text>
            <Text>
              • Attempt to gain unauthorized access to any portion of the
              Service or any systems or networks
            </Text>
            <Text>
              • Use the Service for any commercial purpose without our express
              written consent
            </Text>
            <Text>
              • Collect or store personal data about other users without their
              consent
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            5. Data Privacy and Security
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We are committed to protecting the privacy and security of your
              data. Our data practices are governed by our Privacy Policy, which
              is incorporated into these Terms by reference.
            </Text>
            <Text>
              <strong>Data Collection:</strong> We collect information necessary
              to provide the Service, including school information, student
              records, academic data, and user account information.
            </Text>
            <Text>
              <strong>Data Security:</strong> We implement industry-standard
              security measures to protect your data, including encryption,
              secure servers, and access controls.
            </Text>
            <Text>
              <strong>Data Retention:</strong> We retain your data for as long
              as necessary to provide the Service and comply with legal
              obligations. You may request data deletion at any time.
            </Text>
            <Text>
              <strong>Automatic Data Cleanup:</strong> To maintain system
              performance and reduce storage costs, audit trails and
              notification data are automatically cleaned up and permanently
              deleted after 7 days. This includes system logs, user activity
              records, and notification history. This cleanup process is
              irreversible and cannot be undone.
            </Text>
            <Text>
              <strong>Data Sharing:</strong> We do not sell, trade, or otherwise
              transfer your personal information to third parties without your
              consent, except as required by law.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            6. Educational Data Protection
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              As an educational service, we are committed to protecting student
              data in accordance with applicable educational privacy laws and
              regulations.
            </Text>
            <Text>
              <strong>Student Records:</strong> Student information is protected
              and only accessible to authorized school personnel. Parents and
              guardians may have access to their children's records as permitted
              by law.
            </Text>
            <Text>
              <strong>FERPA Compliance:</strong> We comply with the Family
              Educational Rights and Privacy Act (FERPA) and other applicable
              educational privacy laws.
            </Text>
            <Text>
              <strong>Data Minimization:</strong> We collect only the minimum
              amount of data necessary to provide educational services.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            7. Service Availability and Maintenance
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              We strive to provide reliable and continuous service, but we do
              not guarantee uninterrupted access to the Service. The Service may
              be temporarily unavailable due to maintenance, updates, or
              technical issues.
            </Text>
            <Text>
              <strong>Free Service with Limited Resources:</strong> Result.ng is
              provided as a free service with no external funding. As a
              single-developer project, all resources required to maintain and
              operate the service are personally funded. This means that some
              resources may not be 100% available at all times, and service
              quality may vary based on available resources and infrastructure
              limitations.
            </Text>
            <Text>
              We reserve the right to modify, suspend, or discontinue the
              Service at any time with reasonable notice to users.
            </Text>
            <Text>
              We are not responsible for any loss of data or functionality due
              to service interruptions or maintenance.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            8. Pricing and Feature Availability
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              <strong>Current Free Service:</strong> Result.ng is currently
              provided completely free of charge to all users. There are no
              hidden fees, subscription costs, or premium tiers.
            </Text>
            <Text>
              <strong>Future Feature Development:</strong> To ensure the
              continued development, maintenance, and improvement of the
              service, some new features may become paid features in the future.
              However, this will not affect existing users' ability to perform
              all current functions without payment.
            </Text>
            <Text>
              <strong>Grandfathering Policy:</strong> Users who are already
              using the service will continue to have access to all features
              they currently use, regardless of any future pricing changes. New
              paid features will be clearly marked and will not replace or
              restrict access to existing free functionality.
            </Text>
            <Text>
              <strong>Transparency:</strong> Any changes to pricing or feature
              availability will be communicated clearly to users with advance
              notice, and users will always have the option to continue using
              the service with their current feature set.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            9. Intellectual Property Rights
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              The Service and its original content, features, and functionality
              are owned by us and are protected by international copyright,
              trademark, patent, trade secret, and other intellectual property
              laws.
            </Text>
            <Text>
              You retain ownership of any content you submit to the Service, but
              you grant us a license to use, store, and display such content as
              necessary to provide the Service.
            </Text>
            <Text>
              You may not copy, modify, distribute, sell, or lease any part of
              the Service without our prior written consent.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            10. Limitation of Liability
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              To the maximum extent permitted by law, we shall not be liable for
              any indirect, incidental, special, consequential, or punitive
              damages, including but not limited to loss of profits, data, or
              use.
            </Text>
            <Text>
              Our total liability to you for any claims arising from the use of
              the Service shall not exceed the amount you paid for the Service
              in the twelve months preceding the claim.
            </Text>
            <Text>
              We are not responsible for any decisions made based on the
              information provided through the Service.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            11. Indemnification
          </Heading>
          <Text>
            You agree to indemnify and hold us harmless from any claims,
            damages, losses, or expenses arising from your use of the Service or
            violation of these Terms.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            12. Termination
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              You may terminate your account at any time by contacting us. We
              may terminate or suspend your account immediately, without prior
              notice, for any reason, including breach of these Terms.
            </Text>
            <Text>
              Upon termination, your right to use the Service will cease
              immediately. We may delete your account and data, subject to our
              data retention policies and legal obligations.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            13. Governing Law and Dispute Resolution
          </Heading>
          <VStack gap={4} align="stretch">
            <Text>
              These Terms shall be governed by and construed in accordance with
              the laws of the jurisdiction where our company is registered.
            </Text>
            <Text>
              Any disputes arising from these Terms or the use of the Service
              shall be resolved through binding arbitration in accordance with
              the rules of the relevant arbitration association.
            </Text>
          </VStack>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            14. Changes to Terms
          </Heading>
          <Text>
            We reserve the right to modify these Terms at any time. We will
            notify users of any material changes by posting the new Terms on the
            Service and updating the "Last updated" date. Your continued use of
            the Service after such changes constitutes acceptance of the new
            Terms.
          </Text>
        </Box>

        <Box>
          <Heading as="h2" size="lg" mb={4} color={colors.text.primary}>
            15. Contact Information
          </Heading>
          <Text>
            If you have any questions about these Terms of Service, please
            contact us at:
          </Text>
          <VStack gap={2} align="start" mt={4}>
            <Text>Email: support@result.ng</Text>
          </VStack>
        </Box>

        <Box
          borderTop="1px solid"
          borderColor="gray.200"
          pt={4}
          textAlign="center"
        >
          <Text fontSize="sm" color={colors.text.secondary}>
            By using Result.ng, you acknowledge that you have read, understood,
            and agree to be bound by these Terms of Service.
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default Terms;
